import React, { useEffect, useState } from "react";
import axios from "axios";
import authManager from "../../../utils/authManager";
import { useParams } from "react-router-dom";
import Layout from "../Layout";
import Pagination from "../../ui/Pagination";
import { 
  FaFilePdf, 
  FaFileImage, 
  FaFileAlt, 
  FaDownload,
  FaEye,
  FaSearch,
  FaFilter,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
  FaSyncAlt
} from "react-icons/fa";
import { MdOutlineFileDownload } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";

const BASE_URL = "https://actadcd.in/backend/assets-two/forms/";

const FormUrls = {
  "DEATH APPLICATION FORM": "deathform",
  "MEDICAL AID APPLICATION FOR FAMILY MEMBER": "medical-aid-family",
  "MEDICAL AID APPLICATION FOR MEMBER": "medical-aid",
  "REGISTRATION (RETIREMENT BENEFIT SCHEME)": "retirement",
  "NOMINATION FORM": "nomination",
  "UPGRADATION MEMBERSHIP APPLICATION FORM": "upgradtion",
  "COMPLAINT - FORM": "complaint",
};

// Color mapping for different form types
const getFormColors = (type) => {
  if (type.includes("DEATH")) return {
    header: "from-gray-800 to-gray-900",
    badge: "bg-gray-100 text-gray-800",
    icon: "text-gray-600"
  };
  if (type.includes("MEDICAL AID APPLICATION FOR MEMBER")) return {
    header: "from-blue-800 to-blue-900",
    badge: "bg-blue-100 text-blue-800",
    icon: "text-blue-600"
  };
  if (type.includes("MEDICAL AID APPLICATION FOR FAMILY")) return {
    header: "from-cyan-800 to-cyan-900",
    badge: "bg-cyan-100 text-cyan-800",
    icon: "text-cyan-600"
  };
  if (type.includes("REGISTRATION")) return {
    header: "from-purple-800 to-purple-900",
    badge: "bg-purple-100 text-purple-800",
    icon: "text-purple-600"
  };
  if (type.includes("NOMINATION")) return {
    header: "from-green-800 to-green-900",
    badge: "bg-green-100 text-green-800",
    icon: "text-green-600"
  };
  if (type.includes("UPGRADATION")) return {
    header: "from-amber-800 to-amber-900",
    badge: "bg-amber-100 text-amber-800",
    icon: "text-amber-600"
  };
  if (type.includes("COMPLAINT")) return {
    header: "from-red-800 to-red-900",
    badge: "bg-red-100 text-red-800",
    icon: "text-red-600"
  };
  return {
    header: "from-gray-800 to-gray-900",
    badge: "bg-gray-100 text-gray-800",
    icon: "text-gray-600"
  };
};

const ShowForm = () => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filteredData, setFilteredData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  const { type } = useParams();
  const colors = getFormColors(type);

  const fetchFormData = async (page = 1) => {
    try {
      setLoading(true);
      const offset = (page - 1) * pagination.limit;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}${FormUrls[type]}?limit=${pagination.limit}&offset=${offset}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authManager.getAccessToken()}`,
          },
        }
      );

      if (res.data?.response && Array.isArray(res.data.data)) {
        setData(res.data.data);
        setFilteredData(res.data.data);
        const total = res.data.total || res.data.data.length || 0;
        const limit = res.data.limit || pagination.limit;

        setPagination({
          currentPage: page,
          totalPages: Math.ceil(total / limit),
          limit,
          total,
        });
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Search and filter effect
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredData(data);
    } else {
      const filtered = data.filter(row => {
        return Object.values(row).some(value => 
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  // Sorting function
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    
    const sortedData = [...filteredData].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setFilteredData(sortedData);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="w-3 h-3 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="w-3 h-3 text-blue-600" /> 
      : <FaSortDown className="w-3 h-3 text-blue-600" />;
  };

  // Pagination handler
  const handlePageChange = (page) => {
    if (page !== pagination.currentPage && page > 0 && page <= pagination.totalPages) {
      fetchFormData(page);
    }
  };

  // File renderer
  const renderFileLink = (filename) => {
    const fileExt = filename.split('.').pop().toLowerCase();
    const fileUrl = `${BASE_URL}${filename}`;
    
    const getFileIcon = () => {
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt))
        return <FaFileImage className="w-4 h-4 text-blue-500" />;
      if (fileExt === 'pdf')
        return <FaFilePdf className="w-4 h-4 text-red-500" />;
      return <FaFileAlt className="w-4 h-4 text-gray-500" />;
    };

    return (
      <div className="flex items-center gap-2">
        {getFileIcon()}
        <span className="text-xs text-gray-600 truncate max-w-[100px]">
          {filename.split('/').pop()}
        </span>
        <div className="flex gap-1">
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 text-blue-600 hover:bg-blue-50 rounded transition"
            title="View"
          >
            <FaEye className="w-3.5 h-3.5" />
          </a>
          <a
            href={fileUrl}
            download
            className="p-1 text-green-600 hover:bg-green-50 rounded transition"
            title="Download"
          >
            <MdOutlineFileDownload className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    );
  };

  // Cell renderer
  const renderCell = (key, value, row) => {
    if (value === null || value === undefined || value === "") return "-";

    // Handle arrays (like family_details)
    if (Array.isArray(value)) {
      if (typeof value[0] === "object") {
        return (
          <div className="space-y-1 max-w-xs">
            {value.map((obj, idx) => (
              <div
                key={idx}
                className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-xs hover:bg-gray-100 transition"
              >
                {Object.entries(obj).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <span className="font-medium text-gray-700 capitalize">
                      {k.replaceAll("_", " ")}:
                    </span>
                    <span className="text-gray-600">{v || "-"}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      } else {
        return (
          <div className="flex flex-wrap gap-1">
            {value.map((item, idx) => (
              <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs">
                {item}
              </span>
            ))}
          </div>
        );
      }
    }

    // Handle files
    if (typeof value === "string") {
      const lower = value.toLowerCase();
      if (/\.(webp|jpg|jpeg|png|gif|pdf|doc|docx)$/i.test(lower)) {
        return renderFileLink(value);
      }
    }

    // Format dates
    if (key.toLowerCase().includes("date") && value) {
      return (
        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
          {new Date(value).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })}
        </span>
      );
    }

    // Status badges
    if (key === "status") {
      const statusColors = {
        pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
        approved: "bg-green-100 text-green-800 border-green-200",
        rejected: "bg-red-100 text-red-800 border-red-200",
        processing: "bg-blue-100 text-blue-800 border-blue-200",
      };
      const color = statusColors[value?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200";
      
      return (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${color}`}>
          {value}
        </span>
      );
    }

    // Handle numbers
    if (typeof value === "number" || !isNaN(value)) {
      return <span className="font-mono text-sm">{value}</span>;
    }

    // Default
    return <span className="text-sm">{value?.toString()}</span>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading form data...</p>
        </div>
      </Layout>
    );
  }

  if (!data.length) {
    return (
      <Layout>
        <div className="p-6">
          <div className={`bg-gradient-to-r ${colors.header} text-white p-6 rounded-xl mb-6`}>
            <h1 className="text-2xl font-bold">{type}</h1>
            <p className="text-sm opacity-90 mt-1">Form submissions overview</p>
          </div>
          <div className="text-center py-16 bg-white rounded-xl shadow-sm">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-xl font-semibold text-gray-700">No records found</p>
            <p className="text-sm text-gray-500 mt-2">There are no submissions for this form yet</p>
          </div>
        </div>
      </Layout>
    );
  }

  const columns = Object.keys(data[0]);

  return (
    <Layout>
      <div className="p-4 md:p-6">
        {/* Header Section */}
        <div className={`bg-gradient-to-r ${colors.header} text-white p-6 rounded-xl mb-6 shadow-lg`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{type}</h1>
              <p className="text-sm opacity-90 mt-1">
                Total {pagination.total} submissions • Page {pagination.currentPage} of {pagination.totalPages}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchFormData()}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm flex items-center gap-2 transition"
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search in all fields..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2.5 border rounded-lg text-sm flex items-center gap-2 transition ${
              showFilters ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <FaFilter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-gray-100 to-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-16">
                    #
                  </th>
                  {columns.map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200/50 transition"
                      onClick={() => requestSort(col)}
                    >
                      <div className="flex items-center gap-2">
                        <span>{col.replaceAll("_", " ")}</span>
                        {getSortIcon(col)}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-16">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredData.map((row, i) => (
                  <tr
                    key={row.id || i}
                    className={`hover:bg-blue-50/50 transition ${
                      selectedRow === i ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => setSelectedRow(i)}
                  >
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                      {(pagination.currentPage - 1) * pagination.limit + i + 1}
                    </td>
                    {columns.map((col) => (
                      <td key={col} className="px-4 py-3">
                        {renderCell(col, row[col], row)}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <button className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition">
                        <BsThreeDotsVertical className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer with stats */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-gray-600">
              Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.limit, pagination.total)} of{' '}
              {pagination.total} entries
            </p>
            
            {/* Pagination */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition"
              >
                <FaChevronLeft className="w-4 h-4" />
              </button>
              
              <div className="flex gap-1">
                {[...Array(pagination.totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === pagination.totalPages ||
                    (pageNum >= pagination.currentPage - 2 && pageNum <= pagination.currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 text-sm rounded-lg transition ${
                          pagination.currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === pagination.currentPage - 3 ||
                    pageNum === pagination.currentPage + 3
                  ) {
                    return <span key={pageNum} className="w-8 text-center">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition"
              >
                <FaChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ShowForm;